import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { ORDER_DETAILS_ID } from '.'
import { Element } from '../../../../../common/html/element'
import { Panel } from '../../../../../common/panel'
import { SelectCustom } from '../../../../../common/selectCustom'
import { Table } from '../../../../../common/table'
import { expiryDate } from '../../../../../dashboard/components/currentPlan/helper'
import { formatText } from '../../../../../helpers/value'
import { SCROLL_LAYOUT_ELEMENT_ID } from '../../../../../layout/LayoutContainer'
import { CLASS_HIDE_TABLE_PDF } from '../../../../../utils/common'
import {
  BI_REPORTS,
  COMPLETED_STATUS,
  PAYMENT_METHOD_PACKAGE,
} from '../../constants'
import { DateOrderedCell } from '../../table/DateOrderedCell'
import { OrderStatusCell } from '../../table/OrderStatusCell'
import {
  changeActiveReport,
  selectActiveReport,
  selectBIReportCellData,
  selectBIReportIds,
  selectComprehensiveCellData,
  selectComprehensiveIds,
  selectCreditCellData,
  selectCreditIds,
  selectIsLoading,
  selectReportBIData,
  selectStandardCellData,
  selectStandardIds,
} from '../store/slice'
import classes from './index.module.scss'
import { BIDescription } from './table/BIDescription'
import { CompanyName } from './table/CompanyName'
import { DownloadCell } from './table/DownloadCell'
import { FileType } from './table/FileType'
import { LanguageCell } from './table/LanguageCell'
import { PriceCell } from './table/PriceCell'
import { TypeAndVersion } from './table/TypeAndVersion'

const schema = {
  NO: {
    colId: 'id',
    title: '#',
  },
  COMPANY_NAME: {
    colId: 'organizationName',
    title: 'COMPANY_NAME',
  },
  TYPE: {
    colId: 'fiinGateReportTypeName',
    title: 'TYPE_VERSION',
  },
  DESCRIPTION: {
    colId: 'availableData',
    title: 'DESCRIPTION',
  },
  LANGUAGE: {
    colId: 'languageCode',
    title: 'LANGUAGE',
  },
  CURRENCY: {
    colId: 'currencyCode',
    title: 'CURRENCY',
  },
  ADDITIONAL_CURRENCY: {
    colId: 'additionalCurrencyCode',
    title: 'ADDITIONAL_CURRENCY',
  },
  FILE_TYPE: {
    colId: 'fileType',
    title: 'FILE_TYPE',
  },
  DELIVERY_DATE: {
    colId: 'expectedDeliveryDate',
    title: 'DELIVERY_DATE',
  },
  STATUS: {
    colId: 'status',
    title: 'ORDER_STATUS',
  },
  PRICE: {
    colId: 'priceVND',
    title: 'PRICE',
  },
  REPORT: {
    colId: 'orderDetailId',
    title: 'REPORT',
  },
}

export const getIdsAndGetDataFromRedux = ({
  activeReport,
  creditIds,
  standardIds,
  comprehensiveIds,
  bIReportIds,
}) => {
  switch (activeReport.value) {
    case BI_REPORTS.CREDIT.value:
      return {
        ids: creditIds,
        getDataFromRedux: selectCreditCellData,
      }
    case BI_REPORTS.STANDARD.value:
      return {
        ids: standardIds,
        getDataFromRedux: selectStandardCellData,
      }
    case BI_REPORTS.COMPREHENSIVE.value:
      return {
        ids: comprehensiveIds,
        getDataFromRedux: selectComprehensiveCellData,
      }
    default:
      return {
        ids: bIReportIds,
        getDataFromRedux: selectBIReportCellData,
      }
  }
}

export const BIReport = ({ isPdf = false }) => {
  const dispatch = useDispatch()

  const standardIds = useSelector(selectStandardIds)
  const creditIds = useSelector(selectCreditIds)
  const comprehensiveIds = useSelector(selectComprehensiveIds)
  const bIReportIds = useSelector(selectBIReportIds)
  const isLoading = useSelector(selectIsLoading)
  const activeReport = useSelector(selectActiveReport)
  const data = useSelector(selectReportBIData)

  const { ids, getDataFromRedux } = getIdsAndGetDataFromRedux({
    activeReport,
    creditIds,
    standardIds,
    comprehensiveIds,
    bIReportIds,
  })

  const chooseItem = (item) => {
    dispatch(changeActiveReport(item))
  }

  const renderContent = () => {
    return (
      <>
        {!!bIReportIds.length && (
          <div className="mt2">
            <Element
              className={`${CLASS_HIDE_TABLE_PDF}`}
              style={{
                color: '#0553A5',
                fontSize: 14,
                fontWeight: 600,
                padding: '0px 12px',
                paddingTop: isPdf ? 12 : 0,
              }}
            >
              Business Credit Report
            </Element>
            {!isPdf && (
              <div className={classes.type}>
                <Translate value="userManagement.orderManagement.orderDetail.TYPE" />
                <SelectCustom
                  style={{ minWidth: 150 }}
                  zIndex={13}
                  className="mx2"
                  activeItem={activeReport}
                  data={Object.values(BI_REPORTS)}
                  isI18n
                  isNoWrap
                  chooseItem={chooseItem}
                  scrollId={[SCROLL_LAYOUT_ELEMENT_ID, ORDER_DETAILS_ID]}
                />
              </div>
            )}
            <Table
              ids={ids}
              isLoading={isLoading}
              getDataFromRedux={getDataFromRedux}
              isDisplayScroll={false}
              isNoStickyHead={isPdf}
              noDataText={
                !ids.length
                  ? 'userManagement.orderManagement.orderDetail.NO_DATA'
                  : undefined
              }
              schema={Object.keys(schema)
                .filter((key) => !isPdf || key !== 'REPORT')
                .map((key) => {
                  const result = {
                    colId: schema[key].colId,
                    title:
                      'userManagement.orderManagement.orderDetail.' +
                      schema[key].title,
                    isI18n: true,
                    isHtml: true,
                    thStyle: { textAlign: 'left' },
                    render: (val, { locale, rowId }) => {
                      switch (schema[key].colId) {
                        case schema.COMPANY_NAME.colId:
                          return (
                            <CompanyName
                              val={val}
                              rowId={rowId}
                              getDataFromRedux={getDataFromRedux}
                            />
                          )
                        case schema.TYPE.colId:
                          return (
                            <TypeAndVersion
                              val={val}
                              rowId={rowId}
                              getDataFromRedux={getDataFromRedux}
                            />
                          )
                        case schema.DESCRIPTION.colId:
                          return (
                            <BIDescription
                              val={val}
                              rowId={rowId}
                              getDataFromRedux={getDataFromRedux}
                            />
                          )
                        case schema.LANGUAGE.colId:
                          return <LanguageCell val={val} />
                        case schema.DELIVERY_DATE.colId:
                          return <DateOrderedCell val={val} locale={locale} />
                        case schema.REPORT.colId:
                          const isNotCompletedStatus =
                            data[rowId - 1]?.status !== COMPLETED_STATUS
                          const isExpired =
                            expiryDate(data[rowId - 1]?.expiryDate) <= 0
                          return (
                            <DownloadCell
                              val={val}
                              isDisabled={isNotCompletedStatus}
                              isExpired={isExpired}
                            />
                          )
                        case schema.STATUS.colId:
                          return <OrderStatusCell val={val} />
                        case schema.PRICE.colId:
                          return (
                            <>
                              {data[rowId - 1]?.paymentMethod ===
                              PAYMENT_METHOD_PACKAGE ? (
                                <></>
                              ) : (
                                <PriceCell
                                  val={val}
                                  rowId={rowId}
                                  getDataFromRedux={getDataFromRedux}
                                />
                              )}
                            </>
                          )
                        case schema.FILE_TYPE.colId:
                          return <FileType fileType={val} />
                        default:
                          return formatText(val)
                      }
                    },
                  }

                  return result
                })}
            />
          </div>
        )}
      </>
    )
  }

  if (isPdf) {
    return (
      <Panel
        label="userManagement.orderManagement.orderDetail.ORDER_DETAILS"
        isI18n
      >
        {renderContent()}
      </Panel>
    )
  }

  return <>{renderContent()}</>
}
